import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChannelContext, ConfigContext } from '../../../../context';
import {
  ChannelAppTabAction,
  StoryCard,
  storyCardApi,
  StoryCardPollContent,
  StoryCardStatus,
  StoryCardType,
  StoryStatus,
  useStoryCardChangeLogByIdQuery,
} from '../../../../services';
import { deleteCard } from '../../../../slices';
import {
  AbuseReportType,
  DSAReportType,
  useAbuseReportPopup,
  useAppDispatch,
  useCardEdit,
  useCardTitle,
  useDSAReportPopup,
  useRefreshCommentsFeed,
  useRefreshStoryCardsUserStats,
} from '../../../hooks';
import { getCssVar, layoutPath } from '../../../utils';
import { ChangeLogModal } from '../../ChangeLogModal';
import { ConfirmationModal } from '../../ConfirmationModal';
import { DropdownMenu } from '../../DropdownMenu';
import { IconLabel } from '../../IconLabel';
import { Popup } from '../../Popup';
import { ForwardToChatModal } from './ForwardToChatModal';

import classNames from 'classnames';
import dropdownMenuClasses from '../../DropdownMenu/DropdownMenu.module.scss';
import classes from './CardActionsCommon.module.scss';

export interface CardActionsProps {
  card: StoryCard;
  isHorizontalFeedCard?: boolean;
  isProfileFeedCard?: boolean;
}

interface CardActionsCommonProps extends CardActionsProps {
  isListOpen: boolean;
  setIsListOpen: (isOpen: boolean) => void;
  adminActions?: JSX.Element;
}
export const CardActionsCommon: FunctionComponent<CardActionsCommonProps> = memo(
  ({ card, adminActions, isListOpen, setIsListOpen, isHorizontalFeedCard, isProfileFeedCard }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const [storyCardPollGenerateCSVReport] =
      storyCardApi.endpoints.storyCardPollGenerateCSVReport.useMutation();

    const { refreshCommentsFeed } = useRefreshCommentsFeed();

    const { refreshStoryCardsUserStats } = useRefreshStoryCardsUserStats();

    const { pathname } = useLocation();

    const { cardEditModal, cardEditHandler } = useCardEdit({ storyCard: card, isProfileFeedCard });

    const { appTabs } = useContext(ChannelContext).channel;

    const {
      abuseReport: { enabled: abuseReportEnabled },
      dsaReport: { enabled: dsaReportEnabled },
    } = useContext(ConfigContext).config.elements;

    const cardTitle = useCardTitle(card);

    const {
      id: cardId,
      type: cardType,
      storyId,
      permissions,
      status: cardStatus,
      user,
      story,
      content,
    } = card;

    const { title: storyTitle, status: storyStatus } = story;

    const { id: userId, screenName: userName } = { ...user };

    const { allowToDelete, allowToUpdate, allowToSeeChangeLog } = permissions;

    const cardPublished = useMemo(() => cardStatus === StoryCardStatus.PUBLISHED, [cardStatus]);

    const storyPublished = useMemo(() => storyStatus === StoryStatus.PUBLISHED, [storyStatus]);

    const isNotProfileFeedCard = useMemo(() => !isProfileFeedCard, [isProfileFeedCard]);

    const allowForwardToChat = useMemo(
      () =>
        Boolean(
          isNotProfileFeedCard &&
            storyPublished &&
            cardPublished &&
            cardType !== StoryCardType.POLL &&
            appTabs.find(({ action: { type } }) => type === ChannelAppTabAction.CHATS)
        ),
      [appTabs, cardPublished, cardType, isNotProfileFeedCard, storyPublished]
    );

    const reportArgs = useMemo(() => {
      return {
        storyId,
        storyName: storyTitle,
        postId: cardId,
        postType: cardType,
        postTitle: cardTitle,
        authorId: userId,
        authorName: userName,
      };
    }, [cardId, cardTitle, cardType, storyId, storyTitle, userId, userName]);

    const { abuseReportModal, abuseReportHandler } = useAbuseReportPopup({
      type: AbuseReportType.Post,
      ...reportArgs,
    });

    const { DSAReportModal, DSAReportHandler } = useDSAReportPopup({
      type: DSAReportType.Post,
      ...reportArgs,
    });

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const [isChatForwardOpen, setIsChatForwardOpen] = useState(false);

    const [isChangeLogOpen, setIsChangeLogOpen] = useState(false);

    const { data: changeLogItems = [], isFetching: isChangeLogLoading } =
      useStoryCardChangeLogByIdQuery({ itemId: cardId, storyId }, { skip: !isChangeLogOpen });

    const [loading, setLoading] = useState(false);

    const editHandler = useCallback(async () => {
      setIsListOpen(false);
      cardEditHandler();
    }, [cardEditHandler, setIsListOpen]);

    const deleteHandler = useCallback(() => {
      setIsListOpen(false);
      setIsDeleteOpen(true);
    }, [setIsListOpen]);

    const abuseReportClick = useCallback(() => {
      setIsListOpen(false);
      abuseReportHandler();
    }, [abuseReportHandler, setIsListOpen]);

    const DSAReportClick = useCallback(() => {
      setIsListOpen(false);
      DSAReportHandler();
    }, [DSAReportHandler, setIsListOpen]);

    const cardDelete = useCallback(async () => {
      setLoading(true);
      try {
        const { deleted } = await dispatch(deleteCard({ storyId, storyCardId: cardId })).unwrap();

        toast(t(deleted ? 'cardActions.card-deleted' : 'deleteConfirmation.error-message'));

        if (!deleted) {
          return;
        }

        refreshStoryCardsUserStats();

        refreshCommentsFeed();

        if (pathname === layoutPath(`/details/${storyId}/${cardId}`)) {
          navigate(layoutPath('/'));
        }
      } catch (_) {
        toast.error(t('deleteConfirmation.error-message'));
      } finally {
        setLoading(false);
      }
    }, [
      cardId,
      dispatch,
      navigate,
      pathname,
      refreshCommentsFeed,
      refreshStoryCardsUserStats,
      storyId,
      t,
    ]);

    const forwardToChatClick = useCallback(() => {
      setIsListOpen(false);
      setIsChatForwardOpen(true);
    }, [setIsListOpen]);

    const changeLogHandler = useCallback(() => {
      setIsListOpen(false);
      setIsChangeLogOpen(true);
    }, [setIsListOpen]);

    const exportClick = useCallback(async () => {
      setIsListOpen(false);

      const sent = await storyCardPollGenerateCSVReport({ storyId, storyCardId: cardId }).unwrap();

      toast(t(`common.${sent ? 'report-sent' : 'error-message'}`));
    }, [cardId, setIsListOpen, storyCardPollGenerateCSVReport, storyId, t]);

    const dropdownMenuContent = useMemo(() => {
      return (
        <>
          {adminActions}

          {cardType === StoryCardType.POLL && (content as StoryCardPollContent).allowAnonymous && (
            <IconLabel
              iconId={'table'}
              iconSize={18}
              label={t('cardActions.export')}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={exportClick}
              singleColor
            />
          )}

          {allowForwardToChat && (
            <IconLabel
              iconId={'chats'}
              iconSize={18}
              label={t('cardActions.forward-to-chat')}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={forwardToChatClick}
              singleColor
            />
          )}
          {abuseReportEnabled && isNotProfileFeedCard && (
            <IconLabel
              iconId={'report'}
              iconSize={18}
              label={t('abuseReport.label')}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={abuseReportClick}
              singleColor
            />
          )}
          {dsaReportEnabled && isNotProfileFeedCard && (
            <IconLabel
              iconId={'problem'}
              iconSize={18}
              label={t('DSAReport.label')}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              onClick={DSAReportClick}
              singleColor
            />
          )}
          {allowToSeeChangeLog && (
            <IconLabel
              iconId={'clock'}
              iconSize={18}
              label={t('changeLog.label')}
              onClick={changeLogHandler}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              singleColor
            />
          )}
          {allowToUpdate && (
            <IconLabel
              iconId={'edit'}
              iconSize={18}
              label={t('common.edit')}
              onClick={editHandler}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              singleColor
            />
          )}
          {allowToDelete && (
            <IconLabel
              iconId={'delete'}
              iconSize={18}
              label={t('common.delete')}
              color={getCssVar('--color-danger')}
              onClick={deleteHandler}
              className={dropdownMenuClasses['dropdown-menu__item--small']}
              singleColor
            />
          )}
        </>
      );
    }, [
      DSAReportClick,
      abuseReportClick,
      abuseReportEnabled,
      adminActions,
      allowForwardToChat,
      allowToDelete,
      allowToSeeChangeLog,
      allowToUpdate,
      cardType,
      changeLogHandler,
      content,
      deleteHandler,
      dsaReportEnabled,
      editHandler,
      exportClick,
      forwardToChatClick,
      isNotProfileFeedCard,
      t,
    ]);

    if (
      [adminActions, allowForwardToChat, isNotProfileFeedCard, allowToUpdate, allowToDelete].every(
        (condition) => !Boolean(condition)
      )
    ) {
      return null;
    }

    return (
      <>
        <Popup
          isOpen={isListOpen}
          setIsOpen={setIsListOpen}
          iconId={'dots-menu'}
          ariaLabel={t('common.more-options')}
          color={getCssVar('--action-pane-icon-color')}
          hoverColor={getCssVar('--action-pane-icon-hover-color')}
          bodyTop={'1.5rem'}
          bodyRight={'0.0625rem'}
          body={<DropdownMenu width={'12rem'} content={dropdownMenuContent} />}
          parentClassName={classNames(classes['card-actions'], 'card-actions', {
            [classes['card-actions--horizontal']]: isHorizontalFeedCard,
          })}
        />

        {cardEditModal}

        <ConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          acceptLabel={t('common.delete')}
          onAccept={cardDelete}
          title={t('deleteConfirmation.title')}
          subTitle={t('deleteConfirmation.sub-title')}
          keepOpened={loading}
          loading={loading}
          danger
        />

        {abuseReportModal}

        {DSAReportModal}

        {isChatForwardOpen && (
          <ForwardToChatModal
            isOpen={isChatForwardOpen}
            onClose={() => setIsChatForwardOpen(false)}
            storyCard={card}
          />
        )}

        <ChangeLogModal
          isLoading={isChangeLogLoading}
          isOpen={isChangeLogOpen}
          items={changeLogItems}
          onClose={() => setIsChangeLogOpen(false)}
        />
      </>
    );
  }
);
